import React, { useCallback } from "react";
import { createClasses } from "@bloomberg/media-ui-core";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import typographyStyles from "@bloomberg/media-ui-tokens/css/typography.module.css";
import Link from "next/link";
import styles from "./sectionFrontNav.module.css";
import type { SectionFrontHeaderTab } from "~types/sectionfront";
import { addPageAction } from "~utils/newrelic/newrelicBrowser";
import SectionFrontDisclaimer from "~components/SectionFront/SectionFrontDisclaimer";
import { LocalizedText, TranslatedMessageType } from "~utils/i18n/translate";

type SectionFrontNavProps = {
  sectionFrontName: string;
  sectionTitle: string | TranslatedMessageType;
  activeTab: string;
  headerTabs: Array<SectionFrontHeaderTab>;
};

type SectionFrontNavItemProps = {
  sectionTab: SectionFrontHeaderTab;
  isFirstSection: boolean;
  sectionFrontName?: string;
};

const SECTION_FRONT_PARENT_TAB = new Set<string>(["currencies", "rates-bonds"]);

export function SectionFrontNavItem(props: SectionFrontNavItemProps) {
  const { sectionTab, isFirstSection } = props;

  const handleClick = useCallback(() => {
    trackClickEvent(
      sectionTab.identifier.toLocaleLowerCase().replace(/\s/g, "_")
    );
    addPageAction("mktDataPageNavClick", {
      pageName: sectionTab.identifier,
    });
  }, [sectionTab.identifier]);

  return (
    <li
      className={createClasses(
        sectionTab.hrefPath && styles.link,
        isFirstSection || SECTION_FRONT_PARENT_TAB.has(sectionTab.identifier)
          ? styles.firstLink
          : null
      )}
      key={sectionTab.identifier}
    >
      <Link
        href={sectionTab.hrefPath}
        key={sectionTab.identifier || sectionTab.hrefPath}
        onClick={handleClick}
      >
        <LocalizedText value={sectionTab.title} />
      </Link>
    </li>
  );
}

export function SectionFrontNav(props: SectionFrontNavProps) {
  const { sectionFrontName, sectionTitle, activeTab, headerTabs } = props;

  return (
    <div className={styles.sectionFrontNav}>
      <h1
        className={createClasses([
          typographyStyles.articleHeadlineXxl,
          styles.sectionTitle,
        ])}
      >
        <LocalizedText value={sectionTitle} />
      </h1>
      <hr className={styles.separator} />
      {headerTabs?.length > 0 && (
        <ul
          className={createClasses([
            styles.links,
            typographyStyles.coreShortTextSm,
          ])}
        >
          {headerTabs.map((sectionTab, i) =>
            activeTab !== sectionTab.identifier ? (
              <SectionFrontNavItem
                sectionTab={sectionTab}
                sectionFrontName={sectionFrontName}
                isFirstSection={i === 0}
                key={sectionTab.identifier || sectionTab.hrefPath}
              />
            ) : null
          )}
        </ul>
      )}
      <SectionFrontDisclaimer
        sectionFrontName={sectionFrontName}
        selectedTab={activeTab}
        variant="top"
      />
    </div>
  );
}
